import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  DialogContent,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import { AxiosError } from "axios";
import {
  AccountStatus,
  AdminDetailUserInfoResponse,
  UserTypeResponse,
} from "@veris-health/user-ms/lib/v1";
import { IconName, Option, VrsButton, VrsDialog, VrsIcon, VrsSelect } from "@veris-health/web-core";
import { VrsFormInputField } from "../../../ui/components/VrsFormInputField";
import {
  getIntegrationClient,
  IntegrationClientDefaultThresholds,
  IntegrationClientDetail,
  IntegrationClientSummary,
  listIntegrationClients,
  RotateApiKeyResponse,
  rotateIntegrationClientKey,
  updateIntegrationClient,
} from "../api/actionsApi";
import { getUsers } from "../../Users/api/usersApi";
import SnackbarUtils from "../../../utils/SnackbarUtils";
import { ApiErrorResponse } from "../../../constants";

const editValidationSchema = yup.object().shape({
  outboundEndpoint: yup
    .string()
    .url("Must be a valid URL")
    .nullable()
    .transform((value) => (value === "" ? null : value)),
  outboundSecretReference: yup
    .string()
    .max(255, "Must be at most 255 characters")
    .nullable()
    .transform((value) => (value === "" ? null : value)),
});

const editInitialValues = {
  outboundEndpoint: "",
  outboundSecretReference: "",
  mainOncologist: undefined as number | undefined,
  defaultMinTemp: "",
  defaultMaxTemp: "",
  defaultMinSys: "",
  defaultMaxSys: "",
  defaultMinDia: "",
  defaultMaxDia: "",
  defaultMinHr: "",
  defaultMaxHr: "",
  defaultMinRpm: "",
  defaultMaxRpm: "",
  defaultMinSo2: "",
  defaultMinSteps: "",
};

const buildEditThresholdsPayload = (
  values: typeof editInitialValues,
): IntegrationClientDefaultThresholds | undefined => {
  const thresholds: IntegrationClientDefaultThresholds = {};
  const mappings: Array<{ formKey: keyof typeof editInitialValues; apiKey: keyof IntegrationClientDefaultThresholds }> = [
    { formKey: "defaultMinTemp", apiKey: "default_min_temp" },
    { formKey: "defaultMaxTemp", apiKey: "default_max_temp" },
    { formKey: "defaultMinSys", apiKey: "default_min_sys" },
    { formKey: "defaultMaxSys", apiKey: "default_max_sys" },
    { formKey: "defaultMinDia", apiKey: "default_min_dia" },
    { formKey: "defaultMaxDia", apiKey: "default_max_dia" },
    { formKey: "defaultMinHr", apiKey: "default_min_hr" },
    { formKey: "defaultMaxHr", apiKey: "default_max_hr" },
    { formKey: "defaultMinRpm", apiKey: "default_min_rpm" },
    { formKey: "defaultMaxRpm", apiKey: "default_max_rpm" },
    { formKey: "defaultMinSo2", apiKey: "default_min_so2" },
    { formKey: "defaultMinSteps", apiKey: "default_min_steps" },
  ];
  let hasAny = false;
  mappings.forEach(({ formKey, apiKey }) => {
    const val = values[formKey];
    if (val !== "" && val !== undefined) {
      (thresholds as Record<string, number>)[apiKey] = Number(val);
      hasAny = true;
    }
  });
  return hasAny ? thresholds : undefined;
};

export const EditIntegrationClient = (): JSX.Element => {
  const [showModal, setShowModal] = useState(false);
  const [clients, setClients] = useState<IntegrationClientSummary[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<number | undefined>(undefined);
  const [clientDetail, setClientDetail] = useState<IntegrationClientDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [doctors, setDoctors] = useState<Option[]>([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [rotatedKey, setRotatedKey] = useState<RotateApiKeyResponse | null>(null);
  const [isRotating, setIsRotating] = useState(false);
  const theme = useTheme();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: editInitialValues,
    validationSchema: editValidationSchema,
    onSubmit: (values) => {
      if (!selectedClientId) return;
      setIsSaving(true);
      updateIntegrationClient(selectedClientId, {
        // eslint-disable-next-line camelcase
        outbound_endpoint: values.outboundEndpoint || null,
        // eslint-disable-next-line camelcase
        outbound_secret_reference: values.outboundSecretReference || null,
        // eslint-disable-next-line camelcase
        main_oncologist_id: values.mainOncologist || null,
        // eslint-disable-next-line camelcase
        default_thresholds: buildEditThresholdsPayload(values),
      })
        .then((updated) => {
          setClientDetail(updated);
          SnackbarUtils.success("Integration client updated successfully.");
        })
        .catch((error: AxiosError<ApiErrorResponse>) => {
          const errorMessage =
            error.response?.data?.message ||
            (error.response?.data as { detail?: Array<{ msg: string }> })?.detail?.[0]?.msg ||
            "Something went wrong, please try again";
          SnackbarUtils.error(errorMessage);
        })
        .finally(() => {
          setIsSaving(false);
        });
    },
  });

  const loadClients = useCallback(() => {
    setClientsLoading(true);
    listIntegrationClients()
      .then((response) => {
        setClients(response.clients);
      })
      .catch(() => {
        SnackbarUtils.error("Failed to fetch integration clients.");
      })
      .finally(() => {
        setClientsLoading(false);
      });
  }, []);

  const handleOpenModal = useCallback(() => {
    setShowModal(true);
    loadClients();
  }, [loadClients]);

  const handleClose = useCallback(() => {
    formik.resetForm();
    setShowModal(false);
    setClients([]);
    setSelectedClientId(undefined);
    setClientDetail(null);
    setDoctors([]);
    setRotatedKey(null);
  }, [formik]);

  const clientOptions: Option[] = useMemo(() => {
    return clients.map((c) => ({ label: c.name, value: c.id.toString() }));
  }, [clients]);

  // Fetch detail when client selected
  useEffect(() => {
    if (!selectedClientId) {
      setClientDetail(null);
      setDoctors([]);
      formik.resetForm();
      return;
    }

    setDetailLoading(true);
    setRotatedKey(null);
    getIntegrationClient(selectedClientId)
      .then((detail) => {
        setClientDetail(detail);
        const dt = detail.default_thresholds;
        formik.setValues({
          outboundEndpoint: detail.outbound_endpoint || "",
          outboundSecretReference: detail.outbound_secret_reference || "",
          mainOncologist: detail.main_oncologist_id || undefined,
          defaultMinTemp: dt?.default_min_temp != null ? String(dt.default_min_temp) : "",
          defaultMaxTemp: dt?.default_max_temp != null ? String(dt.default_max_temp) : "",
          defaultMinSys: dt?.default_min_sys != null ? String(dt.default_min_sys) : "",
          defaultMaxSys: dt?.default_max_sys != null ? String(dt.default_max_sys) : "",
          defaultMinDia: dt?.default_min_dia != null ? String(dt.default_min_dia) : "",
          defaultMaxDia: dt?.default_max_dia != null ? String(dt.default_max_dia) : "",
          defaultMinHr: dt?.default_min_hr != null ? String(dt.default_min_hr) : "",
          defaultMaxHr: dt?.default_max_hr != null ? String(dt.default_max_hr) : "",
          defaultMinRpm: dt?.default_min_rpm != null ? String(dt.default_min_rpm) : "",
          defaultMaxRpm: dt?.default_max_rpm != null ? String(dt.default_max_rpm) : "",
          defaultMinSo2: dt?.default_min_so2 != null ? String(dt.default_min_so2) : "",
          defaultMinSteps: dt?.default_min_steps != null ? String(dt.default_min_steps) : "",
        });
      })
      .catch(() => {
        SnackbarUtils.error("Failed to fetch client details.");
      })
      .finally(() => {
        setDetailLoading(false);
      });
  }, [selectedClientId]);

  // Load doctors when hospital is known
  useEffect(() => {
    if (!clientDetail?.hospital_id) {
      setDoctors([]);
      return;
    }

    setDoctorsLoading(true);
    setDoctors([]);
    getUsers({
      type: [UserTypeResponse.MedStaff],
      hospital: clientDetail.hospital_id,
      accountStatus: AccountStatus.Active,
    })
      .then((response) => {
        const doctorOptions = response.items
          .filter((user: AdminDetailUserInfoResponse) =>
            user.user_roles?.some((role) => role.role_name === "doctor"),
          )
          .map((user: AdminDetailUserInfoResponse) => ({
            label: user.name ?? `Doctor ${user.id}`,
            value: user.id.toString(),
          }));
        setDoctors(doctorOptions);
      })
      .catch(() => {
        SnackbarUtils.error("Failed to fetch doctors for this hospital.");
      })
      .finally(() => {
        setDoctorsLoading(false);
      });
  }, [clientDetail?.hospital_id]);

  const handleRotateKey = useCallback(
    (slot: "primary" | "secondary") => {
      if (!selectedClientId) return;
      setIsRotating(true);
      rotateIntegrationClientKey(selectedClientId, { rotate_to: slot })
        .then((response) => {
          setRotatedKey(response);
          SnackbarUtils.success(`${slot.charAt(0).toUpperCase() + slot.slice(1)} key rotated successfully.`);
        })
        .catch((error: AxiosError<ApiErrorResponse>) => {
          const errorMessage =
            error.response?.data?.message ||
            (error.response?.data as { detail?: Array<{ msg: string }> })?.detail?.[0]?.msg ||
            "Something went wrong, please try again";
          SnackbarUtils.error(errorMessage);
        })
        .finally(() => {
          setIsRotating(false);
        });
    },
    [selectedClientId],
  );

  const handleCopyApiKey = useCallback(() => {
    const apiKey = rotatedKey?.api_key;
    if (apiKey) {
      navigator.clipboard.writeText(apiKey);
      SnackbarUtils.success("API key copied to clipboard.");
    }
  }, [rotatedKey]);

  const {
    errors,
    handleSubmit,
    handleChange,
    handleBlur,
    isValid,
    dirty,
    touched,
    values,
    setFieldValue,
  } = formik;

  return (
    <Box pb={2}>
      <VrsButton buttonType="primary" onClick={handleOpenModal}>
        <Typography variant="bodySemibold" fontWeight={400} pt={0.25} pl={2}>
          Edit Existing Integration
        </Typography>
        <Box pl={1} pt={0.75} pr={2}>
          <VrsIcon name={IconName.Edit} size={18} stroke={theme.veris.colors.neutrals.white} />
        </Box>
      </VrsButton>

      <VrsDialog
        title="Edit Integration Client"
        sx={{ "& .MuiPaper-root": { height: "auto" } }}
        open={showModal}
        onClose={handleClose}
        width="650px"
      >
        <DialogContent sx={{ overflowY: "auto", maxHeight: "80vh" }}>
          {/* 1. Client selector */}
          <Box mb={3}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Select Integration Client
            </Typography>
            <VrsSelect
              innerSx={{ padding: theme.spacing(0.75, 0.75, 0.75, 0) }}
              value={selectedClientId?.toString()}
              options={clientOptions}
              placeholder={clientsLoading ? "Loading clients..." : "Choose client"}
              disabled={clientsLoading}
              onSelected={(value) => setSelectedClientId(Number(value))}
              maxHeight="250px"
            />
          </Box>

          {detailLoading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={32} />
            </Box>
          )}

          {clientDetail && !detailLoading && (
            <>
              {/* 2. Configuration section */}
              <FormikProvider value={formik}>
                <form onSubmit={handleSubmit}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Configuration
                  </Typography>

                  {/* Read-only fields */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Name
                      </Typography>
                      <Typography variant="body2">{clientDetail.name}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Hospital ID
                      </Typography>
                      <Typography variant="body2">{clientDetail.hospital_id ?? "—"}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Provider
                      </Typography>
                      <Typography variant="body2">{clientDetail.provider}</Typography>
                    </Grid>
                  </Grid>

                  {/* Editable fields */}
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <VrsFormInputField
                        name="outboundEndpoint"
                        type="text"
                        label="Outbound Endpoint"
                        value={values.outboundEndpoint}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        helperText={
                          touched.outboundEndpoint && errors.outboundEndpoint
                            ? errors.outboundEndpoint
                            : "URL where Veris will send data to the integration partner"
                        }
                        error={!!touched.outboundEndpoint && Boolean(errors.outboundEndpoint)}
                        isValidField={!errors.outboundEndpoint && !!values.outboundEndpoint}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <VrsFormInputField
                        name="outboundSecretReference"
                        type="text"
                        label="Outbound Secret Reference"
                        value={values.outboundSecretReference}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        helperText={
                          touched.outboundSecretReference && errors.outboundSecretReference
                            ? errors.outboundSecretReference
                            : "Key Vault reference for outbound authentication"
                        }
                        error={
                          !!touched.outboundSecretReference &&
                          Boolean(errors.outboundSecretReference)
                        }
                        isValidField={
                          !errors.outboundSecretReference && !!values.outboundSecretReference
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box>
                        <Typography variant="subtitle2">Main Oncologist</Typography>
                        <VrsSelect
                          innerSx={{ padding: theme.spacing(0.75, 0.75, 0.75, 0) }}
                          value={values.mainOncologist?.toString()}
                          options={doctors}
                          placeholder={
                            doctorsLoading ? "Loading doctors..." : "Choose main oncologist"
                          }
                          disabled={doctorsLoading}
                          onSelected={(value) => setFieldValue("mainOncologist", Number(value))}
                          maxHeight="250px"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          Assigned as main oncologist for patients created via EHR orders
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Accordion sx={{ boxShadow: "none", border: `1px solid ${theme.veris.colors.neutrals["grey-2"]}` }}>
                        <AccordionSummary>
                          <Typography variant="subtitle2">Default Thresholds (optional)</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
                            Set default threshold values for patients onboarded via this integration.
                            Leave blank to use system defaults.
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <VrsFormInputField name="defaultMinSys" type="number" label="Systolic BP Min" value={values.defaultMinSys} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                            </Grid>
                            <Grid item xs={6}>
                              <VrsFormInputField name="defaultMaxSys" type="number" label="Systolic BP Max" value={values.defaultMaxSys} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                            </Grid>
                            <Grid item xs={6}>
                              <VrsFormInputField name="defaultMinDia" type="number" label="Diastolic BP Min" value={values.defaultMinDia} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                            </Grid>
                            <Grid item xs={6}>
                              <VrsFormInputField name="defaultMaxDia" type="number" label="Diastolic BP Max" value={values.defaultMaxDia} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                            </Grid>
                            <Grid item xs={6}>
                              <VrsFormInputField name="defaultMinHr" type="number" label="Heart Rate Min" value={values.defaultMinHr} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                            </Grid>
                            <Grid item xs={6}>
                              <VrsFormInputField name="defaultMaxHr" type="number" label="Heart Rate Max" value={values.defaultMaxHr} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                            </Grid>
                            <Grid item xs={6}>
                              <VrsFormInputField name="defaultMinRpm" type="number" label="Resp. Rate Min" value={values.defaultMinRpm} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                            </Grid>
                            <Grid item xs={6}>
                              <VrsFormInputField name="defaultMaxRpm" type="number" label="Resp. Rate Max" value={values.defaultMaxRpm} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                            </Grid>
                            <Grid item xs={6}>
                              <VrsFormInputField name="defaultMinTemp" type="number" label="Temperature Min (°F)" value={values.defaultMinTemp} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                            </Grid>
                            <Grid item xs={6}>
                              <VrsFormInputField name="defaultMaxTemp" type="number" label="Temperature Max (°F)" value={values.defaultMaxTemp} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                            </Grid>
                            <Grid item xs={6}>
                              <VrsFormInputField name="defaultMinSo2" type="number" label="SpO2 Min (%)" value={values.defaultMinSo2} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                            </Grid>
                            <Grid item xs={6}>
                              <VrsFormInputField name="defaultMinSteps" type="number" label="Steps Min" value={values.defaultMinSteps} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>

                    <Grid item xs={12} display="flex" justifyContent="flex-end">
                      <VrsButton buttonType="primary" type="submit" disabled={!isValid || !dirty}>
                        Save Changes
                        {isSaving && (
                          <CircularProgress
                            sx={{
                              marginLeft: theme.spacing(2),
                              color: theme.veris.colors.neutrals.white,
                            }}
                            size={16}
                          />
                        )}
                      </VrsButton>
                    </Grid>
                  </Grid>
                </form>
              </FormikProvider>

              {/* 3. API Key Rotation section */}
              <Box mt={4}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  API Key Rotation
                </Typography>

                <Alert severity="info" sx={{ mb: 2 }}>
                  Use dual-key rotation: rotate the secondary key first, update your integration
                  partner to use it, then rotate the primary key. This ensures zero-downtime key
                  rotation.
                </Alert>

                {rotatedKey ? (
                  <Box>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      This API key will only be shown once. Please copy and store it securely in
                      your vault so you can reprocess failed messages.
                    </Alert>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>Rotated slot:</strong> {rotatedKey.rotated_slot}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: theme.veris.colors.neutrals["grey-1"],
                        borderRadius: 1,
                        p: 2,
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          fontFamily: "monospace",
                          wordBreak: "break-all",
                        }}
                      >
                        {rotatedKey.api_key}
                      </Typography>
                      <VrsButton buttonType="secondary" onClick={handleCopyApiKey}>
                        Copy
                      </VrsButton>
                    </Box>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item>
                      <VrsButton
                        buttonType="secondary"
                        onClick={() => handleRotateKey("secondary")}
                        disabled={isRotating}
                      >
                        Rotate Secondary Key
                        {isRotating && (
                          <CircularProgress sx={{ marginLeft: theme.spacing(1) }} size={16} />
                        )}
                      </VrsButton>
                    </Grid>
                    <Grid item>
                      <VrsButton
                        buttonType="secondary"
                        onClick={() => handleRotateKey("primary")}
                        disabled={isRotating}
                      >
                        Rotate Primary Key
                        {isRotating && (
                          <CircularProgress sx={{ marginLeft: theme.spacing(1) }} size={16} />
                        )}
                      </VrsButton>
                    </Grid>
                  </Grid>
                )}
              </Box>
            </>
          )}
        </DialogContent>
      </VrsDialog>
    </Box>
  );
};
